import { Bot, MessageSquare, Pin, PinOff, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Thread } from "@/api/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  useDeleteThread,
  usePinThread,
} from "@/features/threads/hooks/useThreads";

interface Props {
  thread: Thread;
  /** Nome do agente vinculado (lookup feito no parent). */
  agentName?: string | null;
  /** Nome da team session vinculada (lookup feito no parent). */
  teamSessionName?: string | null;
}

export function ThreadListItem({ thread, agentName, teamSessionName }: Props) {
  const { t } = useTranslation("threads");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const title = thread.title || tc("labels.untitled");
  const isPinned = thread.pinned_at != null;
  const pin = usePinThread();
  const del = useDeleteThread();
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function handleTogglePin(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    e.preventDefault();
    try {
      await pin.mutateAsync({ threadId: thread.id, pinned: !isPinned });
      toast.success(
        isPinned ? t("togglePin.unpinned") : t("togglePin.pinned"),
      );
    } catch (err) {
      toastApiError(err);
    }
  }

  function openDelete(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setDeleteOpen(true);
  }

  async function handleDelete() {
    try {
      await del.mutateAsync(thread.id);
      toast.success(t("delete.success"));
      setDeleteOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <>
      <li
        role="link"
        tabIndex={0}
        onClick={() => navigate(`/threads/${thread.id}`)}
        onKeyDown={(e) => {
          if (e.key === "Enter") navigate(`/threads/${thread.id}`);
        }}
        className={cn(
          "shadow-card flex cursor-pointer items-start gap-3 rounded-2xl border border-line bg-surface p-3 transition hover:border-brand-300 hover:shadow-hover",
          isPinned && "border-brand-200 bg-brand-50/30",
        )}
      >
        <MessageSquare className="mt-1 h-5 w-5 shrink-0 text-ink-500" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate font-medium text-ink-950">
              {title}
            </span>
            {thread.is_sandbox ? (
              <Badge variant="outline" className="shrink-0 text-xs">
                {t("badges.sandbox")}
              </Badge>
            ) : null}
            {isPinned ? (
              <Badge variant="default" className="shrink-0 text-[10px]">
                {t("badges.pinned")}
              </Badge>
            ) : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-500">
            {agentName ? (
              <span className="inline-flex items-center gap-1">
                <Bot className="h-3 w-3" />
                {agentName}
              </span>
            ) : null}
            {teamSessionName ? (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" />
                {teamSessionName}
              </span>
            ) : null}
            <span>{new Date(thread.last_activity_at).toLocaleString()}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={handleTogglePin}
            disabled={pin.isPending}
            aria-label={isPinned ? t("actions.unpin") : t("actions.pin")}
            title={isPinned ? t("actions.unpin") : t("actions.pin")}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-ink-300/20 disabled:opacity-50",
              isPinned ? "text-brand-500" : "text-ink-400 hover:text-brand-500",
            )}
          >
            {isPinned ? (
              <PinOff className="h-3.5 w-3.5" />
            ) : (
              <Pin className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={openDelete}
            disabled={del.isPending}
            aria-label={t("actions.delete")}
            title={t("actions.delete")}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-400 transition-colors hover:bg-danger-50 hover:text-danger-700 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </li>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>
              {tc("actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={del.isPending}
              className="bg-danger-500 from-danger-500 via-danger-500 to-danger-700 text-white hover:bg-danger-700"
            >
              {del.isPending ? t("delete.pending") : t("delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
